import { ChangeDetectorRef, Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, takeUntil } from 'rxjs/operators';
import { CmaApi } from 'src/app/_shared/api/cma.api';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import { ChatBotService } from 'src/app/chat-bot/chat-bot.service';
import { ReportsService } from 'src/app/reports/reports.service';
import { PropertyState } from 'src/app/workspace/state/property.state';
import { environment } from 'src/environments/environment';
import { CmaState } from '../cma.state';

@Component({
  selector: 'app-create-cma-report',
  templateUrl: './create-cma-report.component.html',
  styleUrls: ['./create-cma-report.component.scss'],
})
export class CreateCmaReportComponent implements OnInit {
  @Output() reportFailed: EventEmitter<any> = new EventEmitter<any>();

  onDestroyNotifier$ = new Subject();
  isScreenWidthLarge: boolean = false;
  showPropertyDetailsCard: boolean = false;
  isLoadingCreateReport: boolean = false;
  connectedUsers: any;
  userType: any;
  propertyDetails: any;
  selectedUser: any;
  showBottomSection: boolean = false;
  cmaForm = new FormGroup({
    //  clientName: new FormControl('', Validators.required),
    clientName: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)]),
    clientType: new FormControl('', Validators.required),
    selectedUser: new FormControl(''),
  });
  reportDetails: void;
  reportType: any;
  companyAlias: any;
  userId: string = null;
  userRole: string = null;
  userTrialResponse: any;
  showFreeTrial: boolean = false;
  redirectionMessage: string = '';
  redirectTo: string = '';
  proceedReportCreation: boolean = true;
  queryAddress: string;

  constructor(
    public cmaState: CmaState,
    private cdr: ChangeDetectorRef,
    private reportsService: ReportsService,
    private userState: UserState,
    private cmaApi: CmaApi,
    private snackBar: MatSnackBar,
    private router: Router,
    private appState: AppState,
    private chatBotService: ChatBotService,
    private route:ActivatedRoute,
    private propertyState: PropertyState
  ) { }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.isScreenWidthLarge = window.innerWidth > 768;
  }

  ngOnInit(): void {
    this.isScreenWidthLarge = window.innerWidth > 768;
    this.reset()
    this.route.queryParamMap
    .pipe(takeUntil(this.onDestroyNotifier$))
    .subscribe((params) => {
      if (params.get('city')) {
        this.cmaForm.get('clientType')?.setValue('buyer');
        this.queryAddress = [
          params.get('street'),
          params.get('city'),
          params.get('state'),
          params.get('zip'),
        ]
          .filter(Boolean)
          .join(', ');
      }
    });
    this.userState.userDetail$
      .pipe(
        takeUntil(this.onDestroyNotifier$),
        filter((res) => res !== null),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)), // Only emit when the value changes
        first() // Take only the first valid change and then complete
      )
      .subscribe((userDetail: any) => {
        this.reportType = this.appState.companyInfoValue.appConfig?.cmaReport?.displayName
          ? this.appState.companyInfoValue.appConfig.cmaReport.displayName
          : 'CMA';

        if (userDetail) {
          this.userId = userDetail.id;
          this.userRole = userDetail.userType;
          if (this.reportType != 'CMA' && (this.userRole == 'agent' || this.userRole == 'loanOfficer')) {
            this.checkUserDetails();
          }
        }
      });

    this.companyAlias = this.appState.companyInfoValue.alias;
    this.cmaState.isLoadingCreateReportValue.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.isLoadingCreateReport = res;
      this.cdr.detectChanges();
    });

    this.cmaState.showPropertyDetailsCard$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.showPropertyDetailsCard = res;
      this.cmaState.isLoadingCreateReport = false;
      this.cdr.detectChanges();
    });
    this.reportsService.getConnectedUsers();
    this.userState.connectedUsers.subscribe((data) => {
      this.connectedUsers = data;
    });
    this.userType = this.userState.userDetails?.userType;
  }

  checkUserDetails() {
    if (this.userRole == 'agent' || this.userRole == 'loanOfficer') {
      this.isLoadingCreateReport = true;
      this.chatBotService.checkUserDetails().subscribe((res) => {
        if (!res || !res.data) {
          return;
        }
        this.isLoadingCreateReport = false;

        this.userTrialResponse = res.data;

        if (this.userTrialResponse?.isPhoneVerificationNeeded && !this.userTrialResponse?.phoneVerified) {
          this.redirectionMessage =
            'It looks like your phone number isn’t verified. Please click the link below to verify it, and then return here to continue with your report.';
          this.redirectTo = 'profile';
          this.proceedReportCreation = false;
          return;
        }

        if (this.userTrialResponse?.isPaymentNeeded) {
          if (this.userTrialResponse?.remainingNumberOfFreeReports > 0) {
            this.showFreeTrial = true;
          } else {
            this.showFreeTrial = false;
          }

          if (
            !this.showFreeTrial &&
            this.userTrialResponse?.isPaymentNeeded &&
            !this.userTrialResponse?.paymentMethodAdded
          ) {
            this.redirectionMessage = `Your free report limit has ended. Each additional report will cost $${this.userTrialResponse.amountPerReport}. You haven’t added a valid credit card. Please click the link below to add one and then come back to continue with your report.`;
            this.redirectTo = 'payment';
            this.proceedReportCreation = false;
          } else {
            this.proceedReportCreation = true;
          }
        }
      });
    }
  }

  redirect(path) {
    this.router.navigate(['/profile'], {
      queryParams: {
        redirectTo: path,
        redirectedFrom: 'report',
      },
    });
  }

  closeSheet(action) {
    this.cmaState.isLoadingReport = false;
    this.cmaState.loadingMessage = 'Loading...';
    this.cmaState.listPropertySetValue = null;
    this.cmaState.openCreateReportSheetValue = false;
    this.cmaState.searchAddressValue = null;
    this.cmaState.searchPropertyValue = null;
    this.cmaState.listPropertySetValue = null;
    this.cmaState.listPropertySetCount = null;
    this.cmaState.showPropertyDetailsCardValue = false;
    this.showPropertyDetailsCard = false;
    this.onDestroyNotifier$?.next();
    this.onDestroyNotifier$?.complete();
    this.cmaState.hideReportLoader = false;
    const currentParams = this.router.parseUrl(this.router.url).queryParams;
    if (currentParams['city'] && action == 'reset') {
          this.router.navigate([], {
          queryParams: {},
          replaceUrl: true,
          state: { skipUpdate: true },
      });
    }
  }

  reset(){
    this.cmaState.showPropertyDetailsCardValue = false;
    this.cmaState.propertyDetailsCardValue = null;
    this.cmaState.searchAddressValue = null;
  }

  createReport() {
    if (this.cmaForm.invalid && this.reportType == 'CMA') {
      return;
    }
    this.cmaState.isLoadingReport = true;
    this.cmaState.loadingMessage = 'Creating Report...';
    this.cmaState.hideReportLoader = false;
    let obj = {
      userId: this.userId,
      companyId: this.appState.companyInfoValue.id,
      reportName: this.propertyDetails.address,
      reportCategory: this.companyAlias == 'valuetest' ? 'smartReport' : 'instantCMA',
      livingArea: this.propertyDetails.livingArea,
      subType: this.propertyDetails.subType,
      lotSize: this.propertyDetails.lotSize,
      beds: this.propertyDetails.beds,
      baths: this.propertyDetails.baths,
      pmxPropertyId:
        typeof this.propertyDetails.pmxPropertyId === 'string'
          ? this.propertyDetails.pmxPropertyId
          : String(this.propertyDetails.pmxPropertyId),
      listingId: this.cmaState.propertyDetailsCardValue.ListingId,
      yearBuilt: this.propertyDetails.yearBuilt,
      stories: this.propertyDetails.stories,
      parking: this.propertyDetails.parking,
      monthsBack: this.propertyDetails.monthsBack,
      agentId: '',
      loanOfficerId: '',
      clientName: this.cmaForm?.value?.clientName || null,
      clientType: this.cmaForm?.value?.clientType || null,
      mls: this.propertyDetails.MlsBoard,
      streetAddress: this.cmaState.propertyDetailsCardValue.StreetAddress,
      city: this.cmaState.propertyDetailsCardValue.City,
      state: this.cmaState.propertyDetailsCardValue.StateOrProvince,
      zip: this.cmaState.propertyDetailsCardValue.PostalCode,
      imgUrl: this.propertyDetails.imgUrl ? this.propertyDetails.imgUrl : this.cmaState.propertyDetailsCardValue.imgUrl
        ? this.cmaState.propertyDetailsCardValue.imgUrl
        : environment.listingImageBaseUrlAlt +
        '?ListingId=' +
        (this.cmaState.propertyDetailsCardValue.ListingId || 'null') +
        '&imageName=1&height=219',
      numberOfImages: this.cmaState.propertyDetailsCardValue.numberOfImages,
      source: 'reportsPage',
    };

    if (this.cmaForm?.value?.selectedUser) {
      if (this.userType === 'agent') {
        obj.loanOfficerId = this.cmaForm.value.selectedUser;
        delete obj.agentId;
      } else if (this.userType === 'loanOfficer') {
        obj.agentId = this.cmaForm.value.selectedUser;
        delete obj.loanOfficerId;
      }
    } else {
      delete obj.agentId;
      delete obj.loanOfficerId;
    }

    if (this.reportType != 'CMA') {
      this.cmaApi.createSmartReport(obj).subscribe(
        (res) => {
          if (res?.data?.reportId) {
            if (res?.message) {
              this.openSnackBar(res.message, 'snackbar-success');
            }
            this.router.navigate(['/cma/view'], { queryParams: { id: res.data.reportId } });
          } else {
            this.openSnackBar(res?.message ? res.message : 'Some error occurred', 'snackbar-error');
            this.reportFailed.emit();
          }
          this.closeSheet('');
        },
        (err) => {
          console.log('create report err', err);

          this.openSnackBar(err.error.message, 'snackbar-error');
          this.reportFailed.emit();
          this.closeSheet('');
        }
      );
    } else {
      this.cmaApi.createCMAReport(obj).subscribe(
        (res) => {
          if (res?.data?.reportId) {
            this.router.navigate(['/cma/view'], { queryParams: { id: res.data.reportId } });
          } else {
            this.openSnackBar(res?.message ? res.message : 'Some error occurred', 'snackbar-error');
            this.reportFailed.emit();
          }
          this.closeSheet('');
        },
        (err) => {
          console.log('create report err', err);

          this.openSnackBar(err.error.message, 'snackbar-error');
          this.reportFailed.emit();
          this.closeSheet('');
        }
      );
    }
  }

  propertyDetailsChange(event) {
    this.propertyDetails = event;
  }

  toggleBottomSection() {
    this.showBottomSection = !this.showBottomSection;
  }

  openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }
}
